import { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import '../styles/forums.css';
import { PlayerAvatar } from '../components/PlayerAvatar';
import { BackArrowIcon } from '../components/Icons';
import { ForumEmoticonPicker } from '../components/ForumEmoticonPicker';
import AnnoncesPanel from '../components/competitions/AnnoncesPanel';
import { ForumThreadItem } from '../components/forums/ForumThreadItem';
import { useResponsive } from '../hooks/useResponsive';
import { useApp } from '../contexts/AppContext';
import { useTabReselect } from '../hooks/useTabReselect';
import type { Session } from '../types/app';
import { convertEmoticonsToEmojis, convertForumEmojisToSmileys, insertAtCursor } from '../utils/emoticons';
import {
  clearForumReplyDraft,
  forumReplyDraftKey,
  forumReplyDraftScope,
  loadForumReplyDraft,
  saveForumReplyDraft,
} from '../utils/forumReplyDrafts';

// ============================================================
// Types
// ============================================================

const ANNONCES_FORUM_ID = -2;

interface Forum {
  id: number;
  name: string;
  description?: string;
  kind?: 'public' | 'team' | 'division' | 'annonces';
  teamId?: number;
  teamName?: string;
  divisionId?: number;
  /** Agrégat non-lus (forum équipe). */
  unreadCount?: number;
}

interface ForumThread {
  id: number;
  thread: number;
  author: string;
  authorDisplay: string;
  subject: string;
  date: string;
  replyCount: number;
  lastReply: string | null;
  lastReplyAuthor?: string | null;
  lastReplyAuthorDisplay?: string | null;
  recentAuthors?: { pseudo: string; display: string }[];
  preview?: string;
  sticky?: boolean;
  closed?: boolean;
  unread?: boolean | null;
  unreadCount?: number | null;
}

interface ForumMessage {
  id: number;
  thread: number;
  author: string;
  authorDisplay: string;
  subject: string;
  body: string;
  date: string;
  parent: number;
}

interface ForumOverviewItem {
  forum: { id: number; name: string };
  threads: ForumThread[];
}

interface ForumsPageProps {
  session: Session;
}

import { BACKEND_URL } from '../config';
import { processForumContent, stripHtml } from '../utils/forumContent';
import { formatRelativeDate, formatFullDate } from '../utils/forumDates';

const SYNTHESE_ID = -1;

/** Watermark « Récents » (dernier passage) — distinct des non-lus snapshots. */
function recentsLastSeenKey(space: string, pseudo: string): string {
  return `webtarot.forums.recentsLastSeen.${space}.${pseudo.toLowerCase()}`;
}

function readRecentsLastSeen(space: string, pseudo: string): string | null {
  try {
    return localStorage.getItem(recentsLastSeenKey(space, pseudo));
  } catch {
    return null;
  }
}

function writeRecentsLastSeen(space: string, pseudo: string, value: string): void {
  try {
    localStorage.setItem(recentsLastSeenKey(space, pseudo), value);
  } catch { /* private mode */ }
}

function threadActivityStamp(thread: ForumThread): string {
  return thread.lastReply || thread.date;
}

/** Nouveau pour Récents = activité après watermark ET encore non lu (lu ailleurs ⇒ plus « nouveau »). */
function isRecentsFresh(thread: ForumThread, since: string | null): boolean {
  if (!since) return false;
  if (thread.unread !== true) return false;
  return threadActivityStamp(thread) > since;
}

// ============================================================
// Forum icons — SVG with colored backgrounds
// ============================================================

interface ForumIconDef {
  color: string;
  svg: string;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const FORUM_ICON_MAP: Record<string, ForumIconDef> = {
  'récents':    { color: '#4dd0e1', svg: '<path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'synthese':   { color: '#4dd0e1', svg: '<path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'synthèse':   { color: '#4dd0e1', svg: '<path d="M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'général':    { color: '#ef5350', svg: '<path d="M12 2L4 7v2h16V7L12 2zM4 11v9h16v-9H4zm3 2h10v2H7v-2z" fill="currentColor"/>' },
  'general':    { color: '#ef5350', svg: '<path d="M12 2L4 7v2h16V7L12 2zM4 11v9h16v-9H4zm3 2h10v2H7v-2z" fill="currentColor"/>' },
  'jeu de la carte': { color: '#ab47bc', svg: '<rect x="5" y="2" width="10" height="14" rx="1.5" stroke="currentColor" stroke-width="1.8" fill="none"/><text x="8" y="12" font-size="8" font-weight="700" fill="currentColor">♠</text><rect x="9" y="8" width="10" height="14" rx="1.5" stroke="currentColor" stroke-width="1.8" fill="none" opacity="0.5"/>' },
  'tournois':   { color: '#ffa000', svg: '<path d="M5 3h14v3a7 7 0 01-14 0V3zM3 3h3M18 3h3M12 13v3M8 19h8" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>' },
  'communauté': { color: '#42a5f5', svg: '<circle cx="9" cy="7" r="3" fill="currentColor"/><circle cx="15" cy="7" r="3" fill="currentColor" opacity="0.6"/><path d="M3 19c0-3.3 2.7-6 6-6 1.5 0 2.9.6 4 1.5a6 6 0 014-1.5c3.3 0 6 2.7 6 6" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'communaute': { color: '#42a5f5', svg: '<circle cx="9" cy="7" r="3" fill="currentColor"/><circle cx="15" cy="7" r="3" fill="currentColor" opacity="0.6"/><path d="M3 19c0-3.3 2.7-6 6-6 1.5 0 2.9.6 4 1.5a6 6 0 014-1.5c3.3 0 6 2.7 6 6" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'école wt':   { color: '#66bb6a', svg: '<path d="M12 3L2 8l10 5 10-5-10-5zM4 10v5c0 2 3.6 4 8 4s8-2 8-4v-5" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'ecole wt':   { color: '#66bb6a', svg: '<path d="M12 3L2 8l10 5 10-5-10-5zM4 10v5c0 2 3.6 4 8 4s8-2 8-4v-5" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'webtarot':   { color: '#ff8f00', svg: '<rect x="4" y="3" width="8" height="12" rx="1" stroke="currentColor" stroke-width="1.5" fill="none" transform="rotate(-10 8 9)"/><rect x="12" y="3" width="8" height="12" rx="1" stroke="currentColor" stroke-width="1.5" fill="none" transform="rotate(10 16 9)"/><text x="7" y="11" font-size="6" fill="currentColor">♥</text>' },
  'cours fft':  { color: '#7e57c2', svg: '<path d="M4 19V5a2 2 0 012-2h8a2 2 0 012 2v14M4 19h12M8 3v4h4V3" stroke="currentColor" stroke-width="2" fill="none"/><path d="M18 7h-2v12h4V9a2 2 0 00-2-2z" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'rencontres': { color: '#ff7043', svg: '<circle cx="12" cy="8" r="5" stroke="currentColor" stroke-width="2" fill="none"/><path d="M12 3v2M12 13v2M7 8h2M15 8h2" stroke="currentColor" stroke-width="2"/><path d="M8 16h8v3H8z" stroke="currentColor" stroke-width="1.5" fill="none"/>' },
  'équipe':     { color: '#26c6da', svg: '<circle cx="8" cy="6" r="3" fill="currentColor"/><circle cx="16" cy="6" r="3" fill="currentColor"/><path d="M4 18c0-2.2 1.8-4 4-4h8c2.2 0 4 1.8 4 4" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'equipe':     { color: '#26c6da', svg: '<circle cx="8" cy="6" r="3" fill="currentColor"/><circle cx="16" cy="6" r="3" fill="currentColor"/><path d="M4 18c0-2.2 1.8-4 4-4h8c2.2 0 4 1.8 4 4" stroke="currentColor" stroke-width="2" fill="none"/>' },
  'division':   { color: '#e53935', svg: '<path d="M7 2l5 5-5 5M17 2l-5 5 5 5" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/><line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" stroke-width="2"/>' },
  'annonces':   { color: '#e6b800', svg: '<path d="M12 2a3 3 0 00-3 3v5a3 3 0 006 0V5a3 3 0 00-3-3zM19 10a7 7 0 01-14 0M12 17v4M9 22h6" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/>' },
};

// Emoji fallback
const FORUM_EMOJI_MAP: Record<string, string> = {
  'récents': '📋', 'synthèse': '📋', 'synthese': '📋', 'général': '📢', 'general': '📢',
  'jeu de la carte': '🃏', 'tournois': '🏆',
  'communauté': '👥', 'communaute': '👥',
  'école wt': '🎓', 'ecole wt': '🎓',
  'webtarot': '🎴', 'cours fft': '📚',
  'rencontres': '🏅', 'équipe': '👫', 'equipe': '👫',
  'division': '⚔️', 'annonces': '📣',
};

function getForumIcon(name: string): { type: 'svg'; def: ForumIconDef } | { type: 'emoji'; emoji: string } {
  const lower = name.toLowerCase().trim();
  for (const [key, def] of Object.entries(FORUM_ICON_MAP)) {
    if (lower.includes(key) || lower === key) return { type: 'svg', def };
  }
  for (const [key, emoji] of Object.entries(FORUM_EMOJI_MAP)) {
    if (lower.includes(key) || lower === key) return { type: 'emoji', emoji };
  }
  return { type: 'emoji', emoji: '💬' };
}

function ForumIconEl({ name }: { name: string }) {
  const icon = getForumIcon(name);
  if (icon.type === 'svg') {
    return (
      <div
        className="forums-forum-icon forums-icon-svg"
        style={{ background: hexToRgba(icon.def.color, 0.15), color: icon.def.color }}
        dangerouslySetInnerHTML={{
          __html: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">${icon.def.svg}</svg>`,
        }}
      />
    );
  }
  return <div className="forums-forum-icon">{icon.emoji}</div>;
}

/** Icône rail : avatar d'équipe pour le forum équipe, sinon icône forum. */
function ForumRailIcon({ forum, size = 30 }: { forum: Forum | { name: string }; size?: number }) {
  const teamForum = 'kind' in forum && forum.kind === 'team' ? forum : null;
  if (teamForum) {
    const teamKey = (teamForum.teamName || teamForum.name.replace(/^Équipe\s+/i, '')).trim() || teamForum.name;
    return (
      <div className="forums-forum-icon forums-forum-team-avatar" style={{ width: size, height: size }}>
        <PlayerAvatar
          pseudo={teamKey}
          size={size}
          photoUrl={`${BACKEND_URL}/api/avatars/team/${encodeURIComponent(teamKey)}?size=${size * 2}`}
          className="forums-team-avatar-img"
        />
      </div>
    );
  }
  return <ForumIconEl name={forum.name} />;
}

function teamForumDisplayName(forum: Forum): string {
  if (forum.kind !== 'team') return forum.name;
  const raw = (forum.teamName || forum.name.replace(/^Équipe\s+/i, '')).trim();
  return raw || forum.name;
}

function sumThreadUnread(threads: ForumThread[]): number {
  return threads.reduce((sum, t) => {
    if (t.unread !== true) return sum;
    return sum + (typeof t.unreadCount === 'number' && t.unreadCount > 0 ? t.unreadCount : 1);
  }, 0);
}

/** Compact forum chip for Synthèse (icon + color + name). */
function ForumTag({ name }: { name: string }) {
  const icon = getForumIcon(name);
  const color = icon.type === 'svg' ? icon.def.color : '#78909c';
  return (
    <span
      className="forums-thread-forum-tag"
      style={{ color, background: hexToRgba(color, 0.14) }}
    >
      {icon.type === 'svg' ? (
        <span
          className="forums-thread-forum-tag-icon"
          aria-hidden
          dangerouslySetInnerHTML={{
            __html: `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">${icon.def.svg}</svg>`,
          }}
        />
      ) : (
        <span className="forums-thread-forum-tag-emoji" aria-hidden>{icon.emoji}</span>
      )}
      <span className="forums-thread-forum-tag-name">{name}</span>
    </span>
  );
}

/** Emoji fallback for forum icons — kept for potential future use */
export function getForumEmoji(name: string): string {
  const lower = name.toLowerCase().trim();
  for (const [key, emoji] of Object.entries(FORUM_EMOJI_MAP)) {
    if (lower.includes(key) || lower === key) return emoji;
  }
  return '💬';
}

// ============================================================
// Forum helpers
// ============================================================

function forumSupportsCards(forum: Forum | null): boolean {
  if (!forum) return false;
  if (forum.kind === 'team') return true;
  const lower = forum.name.toLowerCase();
  return lower.includes('jeu de la carte');
}

// Selected card numbers → text notation for insertion
function cardsToNotation(cards: number[]): string {
  const suits: Record<string, number[]> = { C: [], P: [], K: [], T: [], A: [] };
  const excuse = cards.includes(78);
  for (const n of cards) {
    if (n === 78) continue;
    if (n >= 57) suits.A.push(n - 56);
    else if (n >= 43) suits.T.push(n - 42);
    else if (n >= 29) suits.K.push(n - 28);
    else if (n >= 15) suits.P.push(n - 14);
    else suits.C.push(n);
  }
  const suitSymbols: Record<string, string> = { C: 'C', P: 'P', K: 'K', T: 'T', A: 'A' };
  const valueName = (v: number, isAtout: boolean): string => {
    if (!isAtout) {
      if (v === 14) return 'R';
      if (v === 13) return 'D';
      if (v === 12) return 'C';
      if (v === 11) return 'V';
      if (v === 1) return 'As';
    }
    return String(v);
  };
  const lines: string[] = [];
  for (const suit of ['A', 'C', 'P', 'K', 'T'] as const) {
    const vals = suits[suit];
    if (vals.length === 0) continue;
    vals.sort((a, b) => b - a);
    const names = vals.map(v => valueName(v, suit === 'A'));
    lines.push(`${suitSymbols[suit]} : ${names.join(' - ')}`);
  }
  if (excuse) lines.push('Excuse');
  return lines.join('\n');
}

// All cards for the composer grid, organized by suit
const COMPOSER_SUITS: { label: string; symbol: string; cards: { num: number; label: string }[] }[] = [
  {
    label: 'Atouts', symbol: '🌟',
    cards: [
      ...Array.from({ length: 21 }, (_, i) => ({ num: 57 + i, label: String(i + 1) })),
      { num: 78, label: 'Ex' },
    ],
  },
  {
    label: 'Coeur', symbol: '♥',
    cards: [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(v => ({
      num: v, label: v === 14 ? 'R' : v === 13 ? 'D' : v === 12 ? 'C' : v === 11 ? 'V' : String(v),
    })),
  },
  {
    label: 'Pique', symbol: '♠',
    cards: [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(v => ({
      num: 14 + v, label: v === 14 ? 'R' : v === 13 ? 'D' : v === 12 ? 'C' : v === 11 ? 'V' : String(v),
    })),
  },
  {
    label: 'Carreau', symbol: '♦',
    cards: [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(v => ({
      num: 28 + v, label: v === 14 ? 'R' : v === 13 ? 'D' : v === 12 ? 'C' : v === 11 ? 'V' : String(v),
    })),
  },
  {
    label: 'Trèfle', symbol: '♣',
    cards: [14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map(v => ({
      num: 42 + v, label: v === 14 ? 'R' : v === 13 ? 'D' : v === 12 ? 'C' : v === 11 ? 'V' : String(v),
    })),
  },
];

// ============================================================
// Memoized sub-components
// ============================================================

const ThreadItem = memo(function ThreadItem({ thread, onClick, isActive = false, compact = false, forumTag, isRecentsNew = false, isEnter = false }: {
  thread: ForumThread; onClick: () => void; isActive?: boolean; compact?: boolean; forumTag?: string; isRecentsNew?: boolean; isEnter?: boolean;
}) {
  return (
    <ForumThreadItem
      thread={thread}
      onClick={onClick}
      isActive={isActive}
      compact={compact}
      isRecentsNew={isRecentsNew}
      isEnter={isEnter}
      forumTag={forumTag ? <ForumTag name={forumTag} /> : undefined}
    />
  );
});

const ForumItemInner = memo(function ForumItemInner({ forum, isActive, onClick }: {
  forum: Forum; isActive: boolean; onClick: () => void;
}) {
  const isTeam = forum.kind === 'team';
  const label = isTeam ? teamForumDisplayName(forum) : forum.name;
  const unread = isTeam && (forum.unreadCount ?? 0) > 0 ? (forum.unreadCount ?? 0) : 0;

  return (
    <button className={`forums-forum-item${isActive ? ' active' : ''}${isTeam ? ' forums-forum-item--team' : ''}`} onClick={onClick}>
      <ForumRailIcon forum={forum} />
      <div className="forums-forum-info">
        <div className="forums-forum-name">
          <span className="forums-forum-name-text">{label}</span>
          {unread > 0 && (
            <span
              className="forums-team-unread-badge"
              title={`${unread} message${unread > 1 ? 's' : ''} non lu${unread > 1 ? 's' : ''}`}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </div>
        {forum.description && <div className="forums-forum-desc">{forum.description}</div>}
      </div>
    </button>
  );
});

const MessageItem = memo(function MessageItem({ msg, isOP, isOwn, isFirstUnread, isEnter = false }: {
  msg: ForumMessage; isOP: boolean; isOwn: boolean; isFirstUnread?: boolean; isEnter?: boolean;
}) {
  return (
    <article
      id={`forum-msg-${msg.id}`}
      className={`forums-message forums-message--stacked${isOP ? ' original' : ''}${isOwn ? ' own' : ''}${isFirstUnread ? ' first-unread' : ''}${isEnter ? ' forums-message--enter' : ''}`}
    >
      {isFirstUnread && (
        <div className="forums-first-unread-marker" aria-hidden>
          <span>Nouveaux messages</span>
        </div>
      )}
      <div className="forums-message-top">
        <div className="forums-message-sidebar">
          <PlayerAvatar pseudo={msg.author} size={36} />
        </div>
        <div className="forums-message-header">
          <span className="forums-message-author">{msg.authorDisplay}</span>
          <span className="forums-message-date">{formatFullDate(msg.date)}</span>
          {isOP && <span className="forums-message-op-badge">OP</span>}
        </div>
      </div>
      <div className="forums-message-below">
        <div
          className="forums-message-body"
          dangerouslySetInnerHTML={{ __html: processForumContent(msg.body) }}
        />
      </div>
    </article>
  );
});

// ============================================================
// Main component
// ============================================================

function forumActionErrorMessage(json: unknown, status: number): string {
  const err = (json as { error?: string; details?: string } | null)?.error;
  if (err === 'missing_pseudo' || status === 401) return 'Session invalide — reconnectez-vous.';
  if (err === 'not_team_member' || status === 403) return 'Accès refusé à ce forum.';
  if (err === 'invalid_payload' || status === 400) return 'Sujet ou message invalide.';
  if (err === 'thread_closed') return 'Ce sujet est fermé.';
  if (err === 'thread_not_found' || status === 404) return 'Sujet introuvable.';
  if (typeof err === 'string' && err.length > 0 && err.length < 80) return err;
  return `Échec de l'envoi (${status})`;
}

export default function ForumsPage({ session }: ForumsPageProps) {
  const { isCompact, isWide } = useResponsive();
  const { showToast } = useApp();
  const space = session.compet ? 'competition' : 'detente';

  const [windowWidth, setWindowWidth] = useState(() => typeof window !== 'undefined' ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const isTriColumn = isWide && windowWidth >= 1280;

  const [forums, setForums] = useState<Forum[]>([]);
  const [selectedForumId, setSelectedForumId] = useState<number | null>(null);
  /** Forum du fil ouvert — peut différer de selectedForumId (lecture depuis Synthèse). */
  const [readingForumId, setReadingForumId] = useState<number | null>(null);
  const [threads, setThreads] = useState<ForumThread[]>([]);
  const [threadsTotal, setThreadsTotal] = useState(0);
  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ForumMessage[]>([]);
  const [overview, setOverview] = useState<ForumOverviewItem[]>([]);
  /** Watermark dernier passage sur Récents (activité thread, comparable string). */
  const [recentsLastSeen, setRecentsLastSeen] = useState<string | null>(() =>
    session.pseudo ? readRecentsLastSeen(space, session.pseudo) : null,
  );
  /** Seuil pour surbrillance dans la liste Récents (watermark au moment de l’ouverture). */
  const [recentsHighlightSince, setRecentsHighlightSince] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMoreThreads, setLoadingMoreThreads] = useState(false);
  const loadMoreThreadsLock = useRef(false);
  const threadsSentinelRef = useRef<HTMLDivElement>(null);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const pulseWatermarkRef = useRef<string | null>(null);
  const seenThreadKeysRef = useRef<Set<string>>(new Set());
  const seenMsgIdsRef = useRef<Set<number>>(new Set());
  const [enteringThreadKeys, setEnteringThreadKeys] = useState<Set<string>>(() => new Set());
  const [enteringMsgIds, setEnteringMsgIds] = useState<Set<number>>(() => new Set());
  const softRefreshLock = useRef(false);
  const [composeError, setComposeError] = useState<string | null>(null);

  const [showNewThread, setShowNewThread] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [replyBody, setReplyBody] = useState('');
  const [posting, setPosting] = useState(false);
  const [composerCards, setComposerCards] = useState<Set<number>>(new Set());
  const [showComposer, setShowComposer] = useState(false);
  const [showReplyComposer, setShowReplyComposer] = useState(false);
  const [replyComposerCards, setReplyComposerCards] = useState<Set<number>>(new Set());
  const [showReplyEmoticons, setShowReplyEmoticons] = useState(false);
  const [showNewEmoticons, setShowNewEmoticons] = useState(false);
  const newBodyRef = useRef<HTMLTextAreaElement>(null);
  const replyBodyRef = useRef<HTMLTextAreaElement>(null);
  const replyDraftKeyRef = useRef<string | null>(null);
  const replyBodyDraftRef = useRef('');
  replyBodyDraftRef.current = replyBody;

  const buildReplyDraftKey = useCallback((forum: Forum | null, threadId: number | null): string | null => {
    if (!forum || threadId == null || !session.pseudo) return null;
    return forumReplyDraftKey(space, session.pseudo, forumReplyDraftScope(forum), threadId);
  }, [space, session.pseudo]);

  const persistCurrentReplyDraft = useCallback(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const body = replyBodyDraftRef.current;
    if (body.trim()) saveForumReplyDraft(key, body);
    else clearForumReplyDraft(key);
  }, []);

  const resetReplyComposer = useCallback((body = '') => {
    replyBodyDraftRef.current = body;
    setReplyBody(body);
    setReplyComposerCards(new Set());
    setShowReplyComposer(body.length > 0);
    setShowReplyEmoticons(false);
  }, []);

  const switchReplyDraft = useCallback((forum: Forum | null, threadId: number | null) => {
    persistCurrentReplyDraft();
    const nextKey = buildReplyDraftKey(forum, threadId);
    replyDraftKeyRef.current = nextKey;
    const loaded = nextKey ? loadForumReplyDraft(nextKey) : '';
    resetReplyComposer(loaded);
  }, [persistCurrentReplyDraft, buildReplyDraftKey, resetReplyComposer]);

  const clearReplyDraftState = useCallback(() => {
    persistCurrentReplyDraft();
    replyDraftKeyRef.current = null;
    resetReplyComposer('');
  }, [persistCurrentReplyDraft, resetReplyComposer]);

  useEffect(() => {
    const key = replyDraftKeyRef.current;
    if (!key) return;
    const timer = window.setTimeout(() => {
      const body = replyBodyDraftRef.current;
      if (body.trim()) saveForumReplyDraft(key, body);
      else clearForumReplyDraft(key);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [replyBody]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ForumMessage[] | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const messagesTopRef = useRef<HTMLDivElement>(null);
  const [firstUnreadMsgId, setFirstUnreadMsgId] = useState<number | null>(null);

  const [compactLevel, setCompactLevel] = useState<0 | 1 | 2>(0);

  const selectedForum = useMemo(() => forums.find(f => f.id === selectedForumId) || null, [forums, selectedForumId]);
  /** Forum utilisé pour charger / répondre au fil ouvert (Synthèse ou forum courant). */
  const actionForum = useMemo(() => {
    const id = readingForumId ?? selectedForumId;
    return forums.find(f => f.id === id) || null;
  }, [forums, readingForumId, selectedForumId]);
  const selectedThread = useMemo(() => {
    if (selectedThreadId == null) return null;
    const fromList = threads.find(t => t.id === selectedThreadId);
    if (fromList) return fromList;
    for (const item of overview) {
      if (readingForumId != null && item.forum.id !== readingForumId) continue;
      const t = item.threads.find(th => th.id === selectedThreadId);
      if (t) return t;
    }
    return null;
  }, [threads, selectedThreadId, overview, readingForumId]);

  // Build the thread API path depending on forum kind
  const getThreadsPath = useCallback((forum: Forum) => {
    if (forum.kind === 'team' && forum.teamId) {
      return `/api/forums/team/${forum.teamId}/threads`;
    }
    if (forum.kind === 'division' && forum.divisionId) {
      return `/api/forums/division/${forum.divisionId}/threads`;
    }
    return `/api/forums/${space}/${forum.id}/threads`;
  }, [space]);

  // Load forums + overview + team/division forums
  useEffect(() => {
    persistCurrentReplyDraft();
    replyDraftKeyRef.current = null;
    setReplyBody('');
    setReplyComposerCards(new Set());
    setShowReplyComposer(false);
    setShowReplyEmoticons(false);

    setForums([]);
    setSelectedForumId(null);
    setReadingForumId(null);
    setThreads([]);
    setSelectedThreadId(null);
    setMessages([]);
    setOverview([]);
    setSearchResults(null);
    setSearchQuery('');
    setCompactLevel(0);
    setRecentsHighlightSince(null);
    setRecentsLastSeen(session.pseudo ? readRecentsLastSeen(space, session.pseudo) : null);
    pulseWatermarkRef.current = null;
    seenThreadKeysRef.current = new Set();
    seenMsgIdsRef.current = new Set();
    setEnteringThreadKeys(new Set());
    setEnteringMsgIds(new Set());

    let cancelled = false;
    (async () => {
      setLoading(true);

      // Fetch forum list (+ extra for competition) — this unblocks the UI
      const forumsFetch = fetch(`${BACKEND_URL}/api/forums/${space}`);
      const extraFetch = (space === 'competition' && session.pseudo)
        ? fetch(`${BACKEND_URL}/api/forums/extra?pseudo=${encodeURIComponent(session.pseudo)}`).catch(() => null)
        : Promise.resolve(null);

      try {
        const [fr, extra] = await Promise.all([forumsFetch, extraFetch]);
        if (cancelled) return;
        let forumsList: Forum[] = [];
        if (fr.ok) forumsList = ((await fr.json()).forums || []).map((f: Forum) => ({ ...f, kind: 'public' as const }));
        if (extra && extra.ok) {
          const data = await extra.json();
          if (data.teamForum) forumsList.push(data.teamForum);
          if (data.divisionForums) forumsList.push(...data.divisionForums);
        }
        if (space === 'competition') {
          forumsList.push({
            id: ANNONCES_FORUM_ID,
            name: 'Petites annonces',
            kind: 'annonces',
            description: 'Recherche de joueur, recherche d\'équipe, défis ouverts',
          });
        }
        if (!cancelled) {
          setForums(forumsList);
          setLoading(false);
        }
      } catch (err) {
        console.error('[Forums] Load error:', err);
        if (!cancelled) setLoading(false);
      }

      // Fetch overview in background — does NOT block forum list display
      try {
        const ovHeaders: Record<string, string> = {};
        if (space === 'competition' && session.pseudo) {
          ovHeaders['X-User-Pseudo'] = session.pseudo;
        }
        const ov = await fetch(`${BACKEND_URL}/api/forums/overview?space=${space}`, {
          headers: ovHeaders,
        });
        if (ov.ok && !cancelled) {
          const overviewData: ForumOverviewItem[] = (await ov.json()).overview || [];
          setOverview(overviewData);
          for (const item of overviewData) {
            for (const t of item.threads) {
              seenThreadKeysRef.current.add(`ov-${item.forum.id}-t-${t.id}`);
            }
          }
        }
      } catch { /* overview is optional */ }
    })();
    return () => { cancelled = true; };
  }, [space, session.pseudo]);

  const forumAuthHeaders = useCallback((): Record<string, string> => {
    if (space !== 'competition' || !session.pseudo) return {};
    return { 'X-User-Pseudo': session.pseudo };
  }, [space, session.pseudo]);

  const markThreadReadLocal = useCallback((threadId: number, forumId?: number | null) => {
    const clear = (t: ForumThread) => (
      t.id === threadId ? { ...t, unread: false, unreadCount: 0 } : t
    );

    setThreads((prev) => {
      const found = prev.find((t) => t.id === threadId);
      const cleared = found?.unread === true
        ? (typeof found.unreadCount === 'number' && found.unreadCount > 0 ? found.unreadCount : 1)
        : 0;
      if (cleared > 0) {
        setForums((forums) => forums.map((f) => {
          if (f.kind !== 'team') return f;
          if (forumId != null && f.id !== forumId) return f;
          return { ...f, unreadCount: Math.max(0, (f.unreadCount ?? 0) - cleared) };
        }));
      }
      return prev.map(clear);
    });

    setOverview((prev) => prev.map((item) => {
      if (forumId != null && item.forum.id !== forumId) return item;
      return { ...item, threads: item.threads.map(clear) };
    }));
  }, []);

  const flashEnteringThreadKeys = useCallback((keys: string[]) => {
    if (keys.length === 0) return;
    setEnteringThreadKeys((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });
    window.setTimeout(() => {
      setEnteringThreadKeys((prev) => {
        const next = new Set(prev);
        for (const k of keys) next.delete(k);
        return next;
      });
    }, 1400);
  }, []);

  const flashEnteringMsgIds = useCallback((ids: number[]) => {
    if (ids.length === 0) return;
    setEnteringMsgIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    window.setTimeout(() => {
      setEnteringMsgIds((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }, 1400);
  }, []);

  const loadThreads = useCallback(async (forum: Forum, offset = 0) => {
    const appending = offset > 0;
    if (appending) {
      if (loadMoreThreadsLock.current) return;
      loadMoreThreadsLock.current = true;
      setLoadingMoreThreads(true);
    } else {
      setLoading(true);
    }
    try {
      const path = getThreadsPath(forum);
      const res = await fetch(`${BACKEND_URL}${path}?limit=30&offset=${offset}`, {
        headers: forumAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const pageThreads: ForumThread[] = data.threads || [];
        if (offset === 0) {
          setThreads(pageThreads);
          seenThreadKeysRef.current = new Set(
            pageThreads.map((t) => `f-${forum.id}-t-${t.id}`),
          );
        } else {
          setThreads((prev) => {
            const next = [...prev, ...pageThreads];
            for (const t of pageThreads) {
              seenThreadKeysRef.current.add(`f-${forum.id}-t-${t.id}`);
            }
            return next;
          });
        }
        const total = data.total || 0;
        setThreadsTotal(total);
        // Forum équipe : resync exacte seulement si toute la liste tient sur la 1ʳᵉ page.
        if (forum.kind === 'team' && offset === 0 && total <= pageThreads.length) {
          const unreadSum = sumThreadUnread(pageThreads);
          setForums((prev) => prev.map((f) => (
            f.id === forum.id ? { ...f, unreadCount: unreadSum } : f
          )));
        }
      }
    } catch (err) {
      console.error('[Forums] Thread load error:', err);
    } finally {
      if (appending) {
        loadMoreThreadsLock.current = false;
        setLoadingMoreThreads(false);
      } else {
        setLoading(false);
      }
    }
  }, [getThreadsPath, forumAuthHeaders]);

  const softReloadThreads = useCallback(async (forum: Forum) => {
    try {
      const path = getThreadsPath(forum);
      const res = await fetch(`${BACKEND_URL}${path}?limit=30&offset=0`, {
        headers: forumAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      const pageThreads: ForumThread[] = data.threads || [];
      const total = data.total || 0;
      setThreadsTotal(total);
      setThreads((prev) => {
        const pageIds = new Set(pageThreads.map((t) => t.id));
        const extras = prev.filter((t) => !pageIds.has(t.id));
        const entering: string[] = [];
        for (const t of pageThreads) {
          const key = `f-${forum.id}-t-${t.id}`;
          if (seenThreadKeysRef.current.size > 0 && !seenThreadKeysRef.current.has(key)) {
            entering.push(key);
          }
          seenThreadKeysRef.current.add(key);
        }
        if (entering.length) flashEnteringThreadKeys(entering);
        return [...pageThreads, ...extras];
      });
      if (forum.kind === 'team' && total <= pageThreads.length) {
        const unreadSum = sumThreadUnread(pageThreads);
        setForums((prev) => prev.map((f) => (
          f.id === forum.id ? { ...f, unreadCount: unreadSum } : f
        )));
      }
    } catch (err) {
      console.error('[Forums] Soft thread reload error:', err);
    }
  }, [getThreadsPath, forumAuthHeaders, flashEnteringThreadKeys]);

  const loadMessages = useCallback(async (
    forum: Forum,
    thread: ForumThread,
    opts?: { soft?: boolean },
  ) => {
    const soft = opts?.soft === true;
    if (!soft) {
      setLoadingMessages(true);
      setFirstUnreadMsgId(null);
      seenMsgIdsRef.current = new Set();
    }
    try {
      const basePath = getThreadsPath(forum);
      const threadId = thread.thread || thread.id;
      const qs = soft ? '?mark_read=0' : '';
      const res = await fetch(
        `${BACKEND_URL}${basePath.replace(/\/threads$/, '')}/threads/${threadId}${qs}`,
        { headers: forumAuthHeaders() },
      );
      if (res.ok) {
        const data = await res.json();
        const msgs: ForumMessage[] = data.messages || [];

        if (soft) {
          const scrollEl = document.querySelector('.forums-messages-scroll') as HTMLElement | null;
          const nearBottom = !scrollEl
            || (scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < 140);
          const entering: number[] = [];
          for (const m of msgs) {
            if (seenMsgIdsRef.current.size > 0 && !seenMsgIdsRef.current.has(m.id)) {
              entering.push(m.id);
            }
            seenMsgIdsRef.current.add(m.id);
          }
          setMessages(msgs);
          if (entering.length) {
            flashEnteringMsgIds(entering);
            if (nearBottom) {
              window.setTimeout(() => {
                const el = document.querySelector('.forums-messages-scroll');
                if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
              }, 50);
            }
          }
        } else {
          setMessages(msgs);
          seenMsgIdsRef.current = new Set(msgs.map((m) => m.id));
          const jumpId = typeof data.firstUnreadId === 'number' ? data.firstUnreadId : null;
          const firstId = msgs[0]?.id;
          setFirstUnreadMsgId(jumpId && firstId && jumpId !== firstId ? jumpId : null);
          if (data.markedRead) {
            markThreadReadLocal(thread.id, forum.id);
          }
        }
      }
    } catch (err) {
      console.error('[Forums] Message load error:', err);
    } finally {
      if (!soft) setLoadingMessages(false);
    }
  }, [getThreadsPath, forumAuthHeaders, markThreadReadLocal, flashEnteringMsgIds]);

  useEffect(() => {
    if (firstUnreadMsgId == null || loadingMessages || messages.length === 0) return;
    const id = firstUnreadMsgId;
    const t = window.setTimeout(() => {
      const el = document.getElementById(`forum-msg-${id}`);
      el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 50);
    return () => window.clearTimeout(t);
  }, [firstUnreadMsgId, loadingMessages, messages]);

  const refreshOverview = useCallback(async (opts?: { animateNew?: boolean }) => {
    try {
      const ov = await fetch(`${BACKEND_URL}/api/forums/overview?space=${space}`, {
        headers: forumAuthHeaders(),
      });
      if (!ov.ok) return;
      const nextOverview: ForumOverviewItem[] = (await ov.json()).overview || [];
      if (opts?.animateNew) {
        const ovSeen = seenThreadKeysRef.current;
        const firstOverviewPass = ![...ovSeen].some((k) => k.startsWith('ov-'));
        if (firstOverviewPass) {
          for (const item of nextOverview) {
            for (const t of item.threads) {
              ovSeen.add(`ov-${item.forum.id}-t-${t.id}`);
            }
          }
        } else {
          const enterKeys: string[] = [];
          for (const item of nextOverview) {
            for (const t of item.threads) {
              const key = `ov-${item.forum.id}-t-${t.id}`;
              if (!ovSeen.has(key)) {
                enterKeys.push(`f-${item.forum.id}-t-${t.id}`);
                ovSeen.add(key);
              }
            }
          }
          if (enterKeys.length) flashEnteringThreadKeys(enterKeys);
        }
      }
      setOverview(nextOverview);
    } catch { /* optional */ }
  }, [space, forumAuthHeaders, flashEnteringThreadKeys]);

  const applyActivityRefresh = useCallback(async () => {
    if (softRefreshLock.current) return;
    softRefreshLock.current = true;
    try {
      await refreshOverview({ animateNew: true });
      if (selectedForum) {
        await softReloadThreads(selectedForum);
      }
      if (actionForum && selectedThread) {
        await loadMessages(actionForum, selectedThread, { soft: true });
      }
    } finally {
      softRefreshLock.current = false;
    }
  }, [refreshOverview, selectedForum, softReloadThreads, actionForum, selectedThread, loadMessages]);

  const applyActivityRefreshRef = useRef(applyActivityRefresh);
  applyActivityRefreshRef.current = applyActivityRefresh;
  const forumAuthHeadersRef = useRef(forumAuthHeaders);
  forumAuthHeadersRef.current = forumAuthHeaders;

  // Pulse mutualisé : poll toutes les 20s si onglet visible (compétition seulement).
  useEffect(() => {
    if (space !== 'competition') return;
    let cancelled = false;

    const tick = async () => {
      if (cancelled || document.visibilityState !== 'visible') return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/forums/pulse?space=${space}`, {
          headers: forumAuthHeadersRef.current(),
        });
        if (!res.ok || cancelled) return;
        const data = await res.json() as {
          watermark?: string | null;
          teamUnreadCount?: number | null;
        };
        if (typeof data.teamUnreadCount === 'number') {
          setForums((prev) => prev.map((f) => (
            f.kind === 'team' ? { ...f, unreadCount: data.teamUnreadCount ?? 0 } : f
          )));
        }
        const wm = data.watermark;
        if (!wm || typeof wm !== 'string') return;
        if (pulseWatermarkRef.current == null) {
          pulseWatermarkRef.current = wm;
          return;
        }
        if (wm === pulseWatermarkRef.current) return;
        pulseWatermarkRef.current = wm;
        await applyActivityRefreshRef.current();
      } catch { /* pulse optional */ }
    };

    void tick();
    const intervalId = window.setInterval(() => { void tick(); }, 20_000);
    const onVis = () => {
      if (document.visibilityState === 'visible') void tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [space]);

  const selectForum = useCallback((forum: Forum | { id: number; name: string }) => {
    clearReplyDraftState();
    if (forum.id === SYNTHESE_ID) {
      // Figé le watermark courant pour la surbrillance, puis on « catch-up » à l’arrivée de l’overview.
      setRecentsHighlightSince(recentsLastSeen);
      setSelectedForumId(null);
      setReadingForumId(null);
      setSelectedThreadId(null);
      setThreads([]);
      setMessages([]);
      setCompactLevel(0);
      void refreshOverview();
      return;
    }
    setSelectedForumId(forum.id);
    setReadingForumId(null);
    setSelectedThreadId(null);
    setMessages([]);
    setShowNewThread(false);
    setComposeError(null);
    setSearchResults(null);
    setSearchQuery('');
    setCompactLevel(1);
    if (forum.id === ANNONCES_FORUM_ID) return; // Petites annonces : panneau dédié, pas de fils classiques
    // Find full forum object for loadThreads
    const full = forums.find(f => f.id === forum.id);
    if (full) loadThreads(full);
    // Garde l’overview à jour pour la pastille « Récents » pendant la navigation.
    void refreshOverview();
  }, [loadThreads, forums, recentsLastSeen, refreshOverview, clearReplyDraftState]);

  const closeThread = useCallback(() => {
    clearReplyDraftState();
    setSelectedThreadId(null);
    setReadingForumId(null);
    setMessages([]);
    setFirstUnreadMsgId(null);
    setCompactLevel(selectedForumId != null ? 1 : 0);
    setComposeError(null);
  }, [selectedForumId, clearReplyDraftState]);

  const selectThread = useCallback((thread: ForumThread, forumOverride?: { id: number; name: string }) => {
    const fromSynthese = selectedForumId == null && forumOverride != null;
    const fid = forumOverride?.id ?? selectedForumId;
    if (fid == null) return;
    const forum = forums.find(f => f.id === fid);
    if (!forum) return;
    // Re-clic sur le topic actif → mode browse (ferme le fil)
    const sameOpen = selectedThreadId === thread.id
      && (fromSynthese ? readingForumId === fid : selectedForumId === fid);
    if (sameOpen) {
      closeThread();
      return;
    }
    // Depuis Synthèse : rester en Synthèse (liste fusionnée), ne pas basculer de forum
    if (!fromSynthese && selectedForumId !== fid) {
      setSelectedForumId(fid);
      loadThreads(forum);
    }
    setReadingForumId(fid);
    setSelectedThreadId(thread.id);
    setCompactLevel(2);
    setComposeError(null);
    switchReplyDraft(forum, thread.id);
    loadMessages(forum, thread);
  }, [selectedForumId, selectedThreadId, readingForumId, forums, loadThreads, loadMessages, closeThread, switchReplyDraft]);

  const goBack = useCallback(() => {
    if (selectedThreadId != null) {
      closeThread();
    } else if (selectedForumId != null) {
      setSelectedForumId(null);
      setReadingForumId(null);
      setThreads([]);
      setShowNewThread(false);
      setCompactLevel(0);
    }
  }, [selectedThreadId, selectedForumId, closeThread]);

  useTabReselect('forums', () => {
    selectForum({ id: SYNTHESE_ID, name: 'Récents' });
  });

  const createThread = useCallback(async () => {
    if (!selectedForum || !newSubject.trim() || !newBody.trim()) return;
    setPosting(true);
    setComposeError(null);
    const subjectSnap = newSubject.trim();
    const bodySnap = convertForumEmojisToSmileys(newBody.trim());
    try {
      const path = getThreadsPath(selectedForum);
      const res = await fetch(`${BACKEND_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Pseudo': session.pseudo },
        body: JSON.stringify({ subject: subjectSnap, body: bodySnap }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg = forumActionErrorMessage(json, res.status);
        setComposeError(msg);
        showToast({ message: msg, type: 'info' });
        return;
      }
      const newId = (json as { id?: number; thread?: number }).id
        ?? (json as { thread?: number }).thread;
      setNewSubject('');
      setNewBody('');
      setShowNewThread(false);
      setComposerCards(new Set());
      setShowComposer(false);
      setShowNewEmoticons(false);
      await loadThreads(selectedForum);
      if (typeof newId === 'number') {
        const synthetic: ForumThread = {
          id: newId,
          thread: newId,
          author: session.pseudo,
          authorDisplay: session.pseudo,
          subject: subjectSnap,
          date: new Date().toISOString(),
          replyCount: 0,
          lastReply: null,
        };
        setThreads((prev) => (prev.some((t) => t.id === newId) ? prev : [synthetic, ...prev]));
        setSelectedThreadId(newId);
        setCompactLevel(2);
        await loadMessages(selectedForum, synthetic);
      }
    } catch (err) {
      console.error('[Forums] Create error:', err);
      const msg = 'Échec de publication';
      setComposeError(msg);
      showToast({ message: msg, type: 'info' });
    } finally {
      setPosting(false);
    }
  }, [selectedForum, newSubject, newBody, session.pseudo, getThreadsPath, loadThreads, loadMessages, showToast]);

  const replyToThread = useCallback(async () => {
    if (!actionForum || !selectedThread || !replyBody.trim()) return;
    setPosting(true);
    setComposeError(null);
    try {
      const basePath = getThreadsPath(actionForum);
      const threadId = selectedThread.thread || selectedThread.id;
      const res = await fetch(`${BACKEND_URL}${basePath.replace(/\/threads$/, '')}/threads/${threadId}/replies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-User-Pseudo': session.pseudo },
        body: JSON.stringify({
          subject: `Re: ${selectedThread.subject}`,
          body: convertForumEmojisToSmileys(replyBody.trim()),
        }),
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        const msg = forumActionErrorMessage(json, res.status);
        setComposeError(msg);
        showToast({ message: msg, type: 'info' });
        return;
      }
      if (replyDraftKeyRef.current) clearForumReplyDraft(replyDraftKeyRef.current);
      resetReplyComposer('');
      await loadMessages(actionForum, selectedThread);
      if (selectedForum) await loadThreads(selectedForum);
      else await refreshOverview();
    } catch (err) {
      console.error('[Forums] Reply error:', err);
      const msg = 'Échec de la réponse';
      setComposeError(msg);
      showToast({ message: msg, type: 'info' });
    } finally {
      setPosting(false);
    }
  }, [actionForum, selectedForum, selectedThread, replyBody, session.pseudo, getThreadsPath, loadMessages, loadThreads, refreshOverview, showToast, resetReplyComposer]);

  const handleSearch = useCallback((q: string) => {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setSearchResults(null); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/forums/search?q=${encodeURIComponent(q.trim())}&space=${space}&limit=20`);
        if (res.ok) setSearchResults((await res.json()).results || []);
      } catch { setSearchResults([]); }
    }, 400);
  }, [space]);

  const canLoadMoreThreads = Boolean(
    selectedForum && threads.length > 0 && threads.length < threadsTotal,
  );

  const loadMoreThreads = useCallback(() => {
    if (!selectedForum || !canLoadMoreThreads || loadMoreThreadsLock.current) return;
    loadThreads(selectedForum, threads.length);
  }, [selectedForum, canLoadMoreThreads, threads.length, loadThreads]);

  // Sticky en tête (comme legacy / API type ASC) — pas appliqué à la Synthèse fusionnée
  const sortedThreads = useMemo(() =>
    [...threads].sort((a, b) => {
      if (a.sticky !== b.sticky) return a.sticky ? -1 : 1;
      const da = a.lastReply || a.date;
      const db = b.lastReply || b.date;
      return db.localeCompare(da);
    }),
  [threads]);

  // Lazy scroll : charge la page suivante quand le sentinelle entre dans la zone scrollable.
  useEffect(() => {
    const sentinel = threadsSentinelRef.current;
    if (!sentinel || !canLoadMoreThreads) return;
    const root = sentinel.closest('.forums-col-scroll');
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadMoreThreads();
      },
      { root: root instanceof Element ? root : null, rootMargin: '480px 0px', threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [canLoadMoreThreads, loadMoreThreads, sortedThreads.length, selectedForumId]);

  // Stable onClick factories
  const forumClickHandlers = useRef(new Map<number, () => void>());
  const threadClickHandlers = useRef(new Map<string, () => void>());
  useEffect(() => { forumClickHandlers.current.clear(); }, [forums, selectForum]);
  useEffect(() => { threadClickHandlers.current.clear(); }, [threads, selectThread]);

  const getForumClick = useCallback((forum: Forum | { id: number; name: string }) => {
    let h = forumClickHandlers.current.get(forum.id);
    if (!h) { h = () => selectForum(forum); forumClickHandlers.current.set(forum.id, h); }
    return h;
  }, [selectForum]);

  const getThreadClick = useCallback((thread: ForumThread, forumOverride?: { id: number; name: string }) => {
    const key = `${thread.id}-${forumOverride?.id ?? ''}`;
    let h = threadClickHandlers.current.get(key);
    if (!h) { h = () => selectThread(thread, forumOverride); threadClickHandlers.current.set(key, h); }
    return h;
  }, [selectThread]);

  // ============================================================
  // Render helpers
  // ============================================================

  const publicForums = useMemo(() => forums.filter(f => f.kind === 'public' || !f.kind), [forums]);
  const specialForums = useMemo(() => forums.filter(f => f.kind === 'team' || f.kind === 'division'), [forums]);

  const renderForumList = (showSynthese = true) => (
    <>
      {showSynthese && (
        <button
          className={`forums-forum-item forums-synthese-item${selectedForumId == null ? ' active' : ''}`}
          onClick={getForumClick({ id: SYNTHESE_ID, name: 'Récents' })}
        >
          <ForumIconEl name="Récents" />
          <div className="forums-forum-info">
            <div className="forums-forum-name forums-forum-name--recents">
              <span className="forums-forum-name-text">Récents</span>
              {recentsNewCount > 0 && (
                <span
                  className="forums-recents-badge"
                  title={`${recentsNewCount} nouveau${recentsNewCount > 1 ? 'x' : ''} depuis votre dernier passage`}
                >
                  {recentsNewCount > 99 ? '99+' : recentsNewCount}
                </span>
              )}
            </div>
            <div className="forums-forum-desc">Tous forums confondus</div>
          </div>
        </button>
      )}
      {publicForums.map((f) => (
        <ForumItemInner key={f.id} forum={f} isActive={selectedForumId === f.id} onClick={getForumClick(f)} />
      ))}
      {specialForums.length > 0 && (
        <>
          <div className="forums-section-divider">
            <span className="forums-section-divider-label">Mes forums</span>
          </div>
          {specialForums.map((f) => (
            <ForumItemInner key={f.id} forum={f} isActive={selectedForumId === f.id} onClick={getForumClick(f)} />
          ))}
        </>
      )}
      {loading && forums.length === 0 && <div className="forums-loading">Chargement…</div>}
    </>
  );

  const renderThreadList = (compact = false) => (
    <>
      {loading && threads.length === 0 && <div className="forums-loading">Chargement…</div>}
      {sortedThreads.map((t) => (
        <ThreadItem
          key={t.id}
          thread={t}
          compact={compact}
          isActive={selectedThreadId === t.id}
          isEnter={selectedForum != null && enteringThreadKeys.has(`f-${selectedForum.id}-t-${t.id}`)}
          onClick={getThreadClick(t)}
        />
      ))}
      {renderLoadMore()}
      {threads.length === 0 && !loading && !showNewThread && (
        <div className="forums-empty">
          Aucun sujet dans ce forum
          <button className="forums-empty-action" onClick={() => setShowNewThread(true)}>Créer le premier sujet</button>
        </div>
      )}
    </>
  );

  const mergedOverviewThreads = useMemo(() => {
    const items: { thread: ForumThread; forum: { id: number; name: string } }[] = [];
    for (const item of overview) {
      for (const thread of item.threads) {
        items.push({ thread, forum: item.forum });
      }
    }
    items.sort((a, b) => {
      const da = a.thread.lastReply || a.thread.date;
      const db = b.thread.lastReply || b.thread.date;
      return db.localeCompare(da);
    });
    return items;
  }, [overview]);

  const maxOverviewActivity = useMemo(() => {
    let max = '';
    for (const { thread } of mergedOverviewThreads) {
      const a = threadActivityStamp(thread);
      if (a > max) max = a;
    }
    return max;
  }, [mergedOverviewThreads]);

  /** Pastille Récents : activité après dernier passage + encore non lu. Masquée quand Récents est affiché. */
  const recentsNewCount = useMemo(() => {
    if (space !== 'competition' || !recentsLastSeen) return 0;
    if (selectedForumId == null) return 0;
    return mergedOverviewThreads.filter(({ thread }) => isRecentsFresh(thread, recentsLastSeen)).length;
  }, [space, recentsLastSeen, selectedForumId, mergedOverviewThreads]);

  // Sur Récents : figer la surbrillance puis avancer le watermark (efface la pastille).
  useEffect(() => {
    if (selectedForumId != null) return;
    if (!session.pseudo || !maxOverviewActivity) return;

    if (recentsLastSeen == null) {
      setRecentsLastSeen(maxOverviewActivity);
      writeRecentsLastSeen(space, session.pseudo, maxOverviewActivity);
      return;
    }

    setRecentsHighlightSince((prev) => prev ?? recentsLastSeen);

    if (maxOverviewActivity > recentsLastSeen) {
      setRecentsLastSeen(maxOverviewActivity);
      writeRecentsLastSeen(space, session.pseudo, maxOverviewActivity);
    }
  }, [selectedForumId, maxOverviewActivity, recentsLastSeen, session.pseudo, space]);

  const renderOverview = () => (
    <div className="forums-overview">
      {overview.length === 0 && loading && <div className="forums-loading">Chargement…</div>}
      {mergedOverviewThreads.map(({ thread, forum }) => (
        <ThreadItem
          key={`${forum.id}-${thread.id}`}
          thread={thread}
          forumTag={forum.name}
          isActive={selectedThreadId === thread.id && readingForumId === forum.id}
          isRecentsNew={isRecentsFresh(thread, recentsHighlightSince)}
          isEnter={enteringThreadKeys.has(`f-${forum.id}-t-${thread.id}`)}
          onClick={getThreadClick(thread, forum)}
        />
      ))}
      {overview.length === 0 && !loading && (
        <div className="forums-empty-center">
          <div className="forums-empty-center-icon">💬</div>
          <div>Aucune activité récente</div>
        </div>
      )}
    </div>
  );

  const renderMessages = () => (
    <>
      <div ref={messagesTopRef} />
      {loadingMessages && messages.length === 0 && <div className="forums-loading">Chargement…</div>}
      {messages.map((msg, i) => (
        <MessageItem
          key={msg.id}
          msg={msg}
          isOP={i === 0}
          isOwn={msg.author.toLowerCase() === session.pseudo.toLowerCase()}
          isFirstUnread={firstUnreadMsgId === msg.id}
          isEnter={enteringMsgIds.has(msg.id)}
        />
      ))}
    </>
  );

  const toggleCard = useCallback((setFn: React.Dispatch<React.SetStateAction<Set<number>>>, num: number) => {
    setFn(prev => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num); else next.add(num);
      return next;
    });
  }, []);

  const insertCards = useCallback((cards: Set<number>, textRef: React.RefObject<HTMLTextAreaElement | null>, setBody: (v: string) => void, body: string) => {
    if (cards.size === 0) return;
    const notation = cardsToNotation(Array.from(cards));
    const ta = textRef.current;
    if (ta) {
      const pos = ta.selectionStart ?? body.length;
      const before = body.slice(0, pos);
      const after = body.slice(pos);
      const sep = before.length > 0 && !before.endsWith('\n') ? '\n' : '';
      setBody(before + sep + notation + '\n' + after);
    } else {
      setBody(body + (body.length > 0 ? '\n' : '') + notation + '\n');
    }
  }, []);

  const renderComposerCard = useCallback((c: { num: number; label: string }, suitLabel: string, cards: Set<number>, setCards: React.Dispatch<React.SetStateAction<Set<number>>>) => {
    const isTrump = c.num >= 57;
    const isFace = !isTrump && (c.num % 14 >= 11 || c.num % 14 === 0);
    const usesImage = isTrump || isFace;
    const suitClass = c.num >= 57 ? '' : c.num >= 43 ? 'suit-clubs' : c.num >= 29 ? 'suit-diamonds' : c.num >= 15 ? 'suit-spades' : 'suit-hearts';
    const suitSymbols: Record<string, string> = { 'suit-hearts': '♥', 'suit-spades': '♠', 'suit-diamonds': '♦', 'suit-clubs': '♣' };

    return (
      <div
        key={c.num}
        className={`card-composer-card-wrap ${suitClass}${cards.has(c.num) ? ' selected' : ''}`}
        onClick={() => toggleCard(setCards, c.num)}
        title={`${suitLabel} ${c.label}`}
      >
        {usesImage ? (
          <img src={`/cards/${c.num}.png`} alt={c.label} draggable={false} />
        ) : (
          <div className="card-composer-card-pip">
            <span className="card-composer-card-pip-value">{c.label === '1' ? 'A' : c.label}</span>
            <span className="card-composer-card-pip-suit">{suitSymbols[suitClass] || ''}</span>
          </div>
        )}
      </div>
    );
  }, [toggleCard]);

  const renderPreviewCard = (num: number, i: number) => {
    const isTrump = num >= 57;
    const isFace = !isTrump && (num % 14 >= 11 || num % 14 === 0);
    const usesImage = isTrump || isFace;
    const suitClass = num >= 57 ? '' : num >= 43 ? 'prev-clubs' : num >= 29 ? 'prev-diamonds' : num >= 15 ? 'prev-spades' : 'prev-hearts';
    const suitSymbols: Record<string, string> = { 'prev-hearts': '♥', 'prev-spades': '♠', 'prev-diamonds': '♦', 'prev-clubs': '♣' };
    const val = num >= 57 ? (num === 78 ? 'Ex' : String(num - 56))
      : (() => { const v = ((num - 1) % 14) + 1; return v === 14 ? 'R' : v === 13 ? 'D' : v === 12 ? 'C' : v === 11 ? 'V' : v === 1 ? 'A' : String(v); })();
    return (
      <div key={num} className={`card-composer-preview-card ${suitClass}`} style={{ zIndex: i }}>
        {usesImage ? (
          <img src={`/cards/${num}.png`} alt="" draggable={false} />
        ) : (
          <div className="card-composer-preview-pip">
            <span className="card-composer-preview-pip-value">{val}</span>
            <span className="card-composer-preview-pip-suit">{suitSymbols[suitClass] || ''}</span>
          </div>
        )}
      </div>
    );
  };

  const renderCardComposer = (
    cards: Set<number>,
    setCards: React.Dispatch<React.SetStateAction<Set<number>>>,
    onInsert: () => void,
    onClose: () => void,
  ) => (
    <div className="card-composer">
      <div className="card-composer-toolbar">
        <span className="card-composer-toolbar-title">Choisir une main</span>
        <button type="button" className="card-composer-close" onClick={onClose} title="Fermer" aria-label="Fermer le sélecteur de cartes">×</button>
      </div>
      <div className="card-composer-suits">
        {COMPOSER_SUITS.map(suit => (
          <div key={suit.label} className={`card-composer-suit${suit.label === 'Atouts' ? ' suit-trumps' : ''}`}>
            <span className="card-composer-suit-label">{suit.symbol}</span>
            {suit.cards.map(c => renderComposerCard(c, suit.label, cards, setCards))}
          </div>
        ))}
      </div>
      <div className="card-composer-preview">
        {cards.size > 0 ? (
          <>
            <span className="card-composer-preview-label">Main ({cards.size})</span>
            <div className="card-composer-preview-cards">
              {Array.from(cards).sort((a, b) => a - b).map((n, i) => renderPreviewCard(n, i))}
            </div>
          </>
        ) : (
          <span className="card-composer-preview-label">Aucune carte sélectionnée</span>
        )}
        <div className="card-composer-actions">
          <button type="button" className="card-composer-clear" onClick={onClose}>Annuler</button>
          {cards.size > 0 && (
            <button type="button" className="card-composer-clear" onClick={() => setCards(new Set())}>Vider</button>
          )}
          <button type="button" className="card-composer-insert" onClick={onInsert} disabled={cards.size === 0}>Insérer</button>
        </div>
      </div>
    </div>
  );

  const canCompose = forumSupportsCards(actionForum ?? selectedForum);

  const applyComposeText = useCallback((
    nextRaw: string,
    textarea: HTMLTextAreaElement | null,
    setBody: (v: string) => void,
  ) => {
    const next = convertEmoticonsToEmojis(nextRaw);
    setBody(next);
    if (!textarea || next === nextRaw) return;
    const delta = next.length - nextRaw.length;
    const caret = Math.max(0, (textarea.selectionStart ?? next.length) + delta);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(caret, caret);
    });
  }, []);

  const insertReplyEmoji = useCallback((emoji: string) => {
    const { next, caret } = insertAtCursor(replyBody, emoji, replyBodyRef.current);
    setReplyBody(next);
    setShowReplyEmoticons(false);
    requestAnimationFrame(() => {
      const ta = replyBodyRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }, [replyBody]);

  const insertNewEmoji = useCallback((emoji: string) => {
    const { next, caret } = insertAtCursor(newBody, emoji, newBodyRef.current);
    setNewBody(next);
    setShowNewEmoticons(false);
    requestAnimationFrame(() => {
      const ta = newBodyRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }, [newBody]);

  const renderReplyForm = () => (
    <div className="forums-reply-form">
      {composeError && <div className="forums-compose-error" role="alert">{composeError}</div>}
      <textarea
        ref={replyBodyRef} className="forums-reply-input" placeholder="Écrire une réponse…"
        value={replyBody}
        onChange={(e) => {
          if (composeError) setComposeError(null);
          applyComposeText(e.target.value, replyBodyRef.current, setReplyBody);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); replyToThread(); } }}
        rows={4}
      />
      <div className="forums-compose-tools">
        <ForumEmoticonPicker
          open={showReplyEmoticons}
          onToggle={() => { setShowReplyEmoticons((v) => !v); setShowReplyComposer(false); }}
          onSelect={insertReplyEmoji}
        />
        {canCompose && (
          showReplyComposer
            ? renderCardComposer(
                replyComposerCards,
                setReplyComposerCards,
                () => {
                  insertCards(replyComposerCards, replyBodyRef, setReplyBody, replyBody);
                  setReplyComposerCards(new Set());
                  setShowReplyComposer(false);
                },
                () => { setReplyComposerCards(new Set()); setShowReplyComposer(false); },
              )
            : (
              <button
                type="button"
                className="card-composer-toggle"
                onClick={() => { setShowReplyComposer(true); setShowReplyEmoticons(false); }}
              >
                🃏 Insérer une main
              </button>
            )
        )}
      </div>
      <div className="forums-reply-actions">
        <span className="forums-reply-hint">Ctrl+Entrée pour envoyer</span>
        <button className="forums-reply-send" onClick={replyToThread} disabled={posting || !replyBody.trim()}>
          {posting ? 'Envoi…' : 'Répondre'}
        </button>
      </div>
    </div>
  );

  const closeNewThreadModal = useCallback(() => {
    setShowNewThread(false);
    setNewSubject('');
    setNewBody('');
    setComposerCards(new Set());
    setShowComposer(false);
    setShowNewEmoticons(false);
    setComposeError(null);
  }, []);

  const openNewThread = useCallback(() => {
    setComposeError(null);
    setShowNewThread(true);
  }, []);

  const renderNewThreadBtn = () => (
    <button
      type="button"
      className="forums-new-btn"
      onClick={openNewThread}
      title="Nouveau sujet"
    >
      <span className="forums-new-btn-plus" aria-hidden>+</span>
      <span className="forums-new-btn-label">Nouveau</span>
    </button>
  );

  // Échap : panneau cartes → modal nouveau sujet → fermer le fil
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showComposer) {
        e.preventDefault();
        setComposerCards(new Set());
        setShowComposer(false);
        return;
      }
      if (showReplyComposer) {
        e.preventDefault();
        setReplyComposerCards(new Set());
        setShowReplyComposer(false);
        return;
      }
      if (showNewEmoticons) { setShowNewEmoticons(false); return; }
      if (showReplyEmoticons) { setShowReplyEmoticons(false); return; }
      if (showNewThread) {
        e.preventDefault();
        closeNewThreadModal();
        return;
      }
      if (selectedThreadId != null) {
        e.preventDefault();
        closeThread();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    showComposer, showReplyComposer, showNewEmoticons, showReplyEmoticons,
    showNewThread, selectedThreadId, closeNewThreadModal, closeThread,
  ]);

  const renderNewThreadModal = () => {
    if (!showNewThread) return null;
    return (
      <div className="forums-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeNewThreadModal(); }}>
        <div className="forums-modal">
          <div className="forums-modal-header">
            {selectedForum ? <ForumRailIcon forum={selectedForum} /> : <ForumIconEl name="" />}
            <h3>Nouveau sujet — {selectedForum ? (selectedForum.kind === 'team' ? teamForumDisplayName(selectedForum) : selectedForum.name) : ''}</h3>
            <button className="forums-modal-close" onClick={closeNewThreadModal}>×</button>
          </div>
          <div className="forums-modal-body">
            {composeError && <div className="forums-compose-error" role="alert">{composeError}</div>}
            <input type="text" placeholder="Titre du sujet" value={newSubject}
              onChange={(e) => { setNewSubject(e.target.value); if (composeError) setComposeError(null); }} className="forums-input" autoFocus />
            <textarea ref={newBodyRef} placeholder="Votre message…" value={newBody}
              onChange={(e) => {
                if (composeError) setComposeError(null);
                applyComposeText(e.target.value, newBodyRef.current, setNewBody);
              }}
              className="forums-textarea" rows={6} />
            <div className="forums-compose-tools">
              <ForumEmoticonPicker
                open={showNewEmoticons}
                onToggle={() => { setShowNewEmoticons((v) => !v); setShowComposer(false); }}
                onSelect={insertNewEmoji}
              />
              {canCompose && (
                showComposer
                  ? renderCardComposer(
                      composerCards,
                      setComposerCards,
                      () => {
                        insertCards(composerCards, newBodyRef, setNewBody, newBody);
                        setComposerCards(new Set());
                        setShowComposer(false);
                      },
                      () => { setComposerCards(new Set()); setShowComposer(false); },
                    )
                  : (
                    <button
                      type="button"
                      className="card-composer-toggle"
                      onClick={() => { setShowComposer(true); setShowNewEmoticons(false); }}
                    >
                      🃏 Insérer une main de cartes
                    </button>
                  )
              )}
            </div>
          </div>
          <div className="forums-modal-footer">
            <button className="forums-cancel-btn" onClick={closeNewThreadModal}>Annuler</button>
            <button className="forums-submit-btn" onClick={() => { createThread(); }} disabled={posting || !newSubject.trim() || !newBody.trim()}>
              {posting ? 'Envoi…' : 'Publier'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLoadMore = () => {
    if (!canLoadMoreThreads && !loadingMoreThreads) return null;
    return (
      <div
        ref={threadsSentinelRef}
        className="forums-load-more-sentinel"
        aria-busy={loadingMoreThreads}
      >
        {loadingMoreThreads ? (
          <div className="forums-load-more-status">Chargement…</div>
        ) : (
          <div className="forums-load-more-status forums-load-more-status--hint">
            {threadsTotal - threads.length} plus anciens…
          </div>
        )}
      </div>
    );
  };

  const renderSearchResults = () => {
    if (!searchResults) return null;
    return (
      <div className="forums-search-results">
        {searchResults.length === 0 ? (
          <div className="forums-empty">Aucun résultat pour « {searchQuery} »</div>
        ) : searchResults.map((r) => (
          <div key={r.id} className="forums-search-result-item">
            <div className="forums-search-result-subject">{r.subject}</div>
            <div className="forums-search-result-meta">
              <span>{r.authorDisplay}</span> · <span>{formatRelativeDate(r.date)}</span>
            </div>
            <div className="forums-search-result-body">{stripHtml(r.body).substring(0, 180)}…</div>
          </div>
        ))}
      </div>
    );
  };

  const renderThreadHeader = () => selectedThread && (
    <div className="forums-thread-header">
      <div className="forums-thread-header-main">
        <h2 className="forums-thread-title">{selectedThread.subject}</h2>
        <div className="forums-thread-header-meta">
          <PlayerAvatar pseudo={selectedThread.author} size={24} />
          <span className="forums-thread-header-author">{selectedThread.authorDisplay}</span>
          <span className="forums-thread-header-date">{formatFullDate(selectedThread.date)}</span>
          {selectedThread.closed && <span className="forums-closed-badge">🔒 Fermé</span>}
        </div>
      </div>
      <button
        type="button"
        className="forums-close-thread-btn"
        onClick={closeThread}
        title="Fermer le fil — revenir à la liste"
        aria-label="Fermer le fil"
      >
        ×
      </button>
    </div>
  );

  // ============================================================
  // COMPACT
  // ============================================================
  if (isCompact) {
    return (
      <div className="forums-container layout-compact">
        {compactLevel === 0 && (
          <div className="forums-panel-full">
            <div className="forums-col-header">
              <h2>Forums</h2>
              <span className="forums-space-badge">{session.compet ? 'Compétition' : 'Détente'}</span>
            </div>
            <div className="forums-search-bar">
              <input type="text" placeholder="Rechercher…" value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)} className="forums-search-input" />
            </div>
            <div className="forums-col-scroll">
              {searchResults !== null ? renderSearchResults() : renderForumList(false)}
            </div>
          </div>
        )}
        {compactLevel === 1 && selectedForum && selectedForum.kind === 'annonces' && (
          <div className="forums-panel-full" style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="forums-col-header">
              <button className="forums-back-btn" onClick={goBack}><BackArrowIcon size={18} /></button>
            </div>
            <AnnoncesPanel session={session} />
          </div>
        )}
        {compactLevel === 1 && selectedForum && selectedForum.kind !== 'annonces' && (
          <div className="forums-panel-full">
            <div className="forums-col-header">
              <button className="forums-back-btn" onClick={goBack}><BackArrowIcon size={18} /></button>
              <h2>{selectedForum.kind === 'team' ? teamForumDisplayName(selectedForum) : selectedForum.name}</h2>
              {renderNewThreadBtn()}
            </div>
            <div className="forums-col-scroll">{renderThreadList()}</div>
          </div>
        )}
        {compactLevel === 2 && selectedThread && (
          <div className="forums-panel-full">
            <div className="forums-col-header">
              <button className="forums-back-btn" onClick={goBack}><BackArrowIcon size={18} /></button>
              <div className="forums-col-header-info">
                <h2>{selectedThread.subject}</h2>
                <span className="forums-col-header-sub">{selectedThread.authorDisplay}</span>
              </div>
            </div>
            <div className="forums-col-scroll forums-messages-scroll">{renderMessages()}</div>
            {!selectedThread.closed && renderReplyForm()}
          </div>
        )}
        {renderNewThreadModal()}
      </div>
    );
  }

  // ============================================================
  // TRI-COLUMN (≥1280px)
  // ============================================================
  if (isTriColumn) {
    const isReading = selectedThreadId != null;
    return (
      <div className={`forums-container layout-tri${isReading ? ' layout-tri--read' : ' layout-tri--browse'}`}>
        <div className="forums-col forums-col-forums">
          <div className="forums-col-header">
            <h2>Forums</h2>
            <span className="forums-space-badge">{session.compet ? 'Compétition' : 'Détente'}</span>
          </div>
          <div className="forums-search-bar sidebar-search">
            <input type="text" placeholder="Rechercher…" value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)} className="forums-search-input" />
          </div>
          <div className="forums-col-scroll">
            {searchResults !== null ? renderSearchResults() : renderForumList()}
          </div>
        </div>
        <div className="forums-col forums-col-threads">
          {selectedForum && selectedForum.kind === 'annonces' ? (
            <AnnoncesPanel session={session} />
          ) : selectedForum ? (
            <>
              <div className="forums-col-header">
                <h2>{selectedForum.kind === 'team' ? teamForumDisplayName(selectedForum) : selectedForum.name}</h2>
                {renderNewThreadBtn()}
              </div>
              <div className="forums-col-scroll">{renderThreadList()}</div>
            </>
          ) : (
            <>
              <div className="forums-col-header"><h2>Récents</h2></div>
              <div className="forums-col-scroll">{renderOverview()}</div>
            </>
          )}
        </div>
        {isReading && (
          <div className="forums-col forums-col-messages">
            {selectedThread ? (
              <>
                {renderThreadHeader()}
                <div className="forums-col-scroll forums-messages-scroll">{renderMessages()}</div>
                {!selectedThread.closed && renderReplyForm()}
              </>
            ) : null}
          </div>
        )}
        {renderNewThreadModal()}
      </div>
    );
  }

  // ============================================================
  // BI-COLUMN (641–1279px)
  // ============================================================
  return (
    <div className="forums-container layout-bi">
      {selectedThreadId == null ? (
        <>
          <div className="forums-col forums-col-forums">
            <div className="forums-col-header">
              <h2>Forums</h2>
              <span className="forums-space-badge">{session.compet ? 'Compétition' : 'Détente'}</span>
            </div>
            <div className="forums-search-bar sidebar-search">
              <input type="text" placeholder="Rechercher…" value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)} className="forums-search-input" />
            </div>
            <div className="forums-col-scroll">
              {searchResults !== null ? renderSearchResults() : renderForumList()}
            </div>
          </div>
          <div className="forums-col forums-col-main">
            {selectedForum && selectedForum.kind === 'annonces' ? (
              <AnnoncesPanel session={session} />
            ) : selectedForum ? (
              <>
                <div className="forums-col-header">
                  <h2>{selectedForum.kind === 'team' ? teamForumDisplayName(selectedForum) : selectedForum.name}</h2>
                  {renderNewThreadBtn()}
                </div>
                <div className="forums-col-scroll">{renderThreadList()}</div>
              </>
            ) : (
              <>
                <div className="forums-col-header"><h2>Récents</h2></div>
                <div className="forums-col-scroll">{renderOverview()}</div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="forums-col forums-col-threads">
            <div className="forums-col-header">
              <button className="forums-back-btn" onClick={goBack}><BackArrowIcon size={18} /></button>
              <h2>{selectedForum?.name || 'Récents'}</h2>
              {selectedForum && renderNewThreadBtn()}
            </div>
            <div className="forums-col-scroll">
              {selectedForum ? renderThreadList(true) : renderOverview()}
            </div>
          </div>
          <div className="forums-col forums-col-messages">
            {selectedThread ? (
              <>
                {renderThreadHeader()}
                <div className="forums-col-scroll forums-messages-scroll">{renderMessages()}</div>
                {!selectedThread.closed && renderReplyForm()}
              </>
            ) : (
              <div className="forums-empty-center">
                <div className="forums-empty-center-icon">💬</div>
                <div>Sélectionnez un sujet</div>
              </div>
            )}
          </div>
        </>
      )}
      {renderNewThreadModal()}
    </div>
  );
}
